import { tenantTeaserImageSize } from '@/helpers/ImageUrls'
import { getTenant } from '@/helpers/Tenant'
import { ticketGroupColors } from '@/helpers/TicketGroupsColors'
import type { TenantConfig, ThemeOptions } from '@/types/TenantConfig'
import Color from 'color'
import merge from 'lodash/merge'

export default function initializeThemeOptions(slug: string) {
  const properties = themeOptions(getTenant(slug))

  // Insert the CSS Custom Properties <style> at the top of the document <head>
  // so that themes' stylesheets may override them.
  // TODO Use CSS Layers to group styles into precedence layers; reset native elements, layout, components, themes.
  for (const key in properties) {
    document.documentElement.style.setProperty(`--${key}`, properties[key])
  }
}

export function themeOptions(tenant: TenantConfig): Dictionary {
  let theme = merge(defaultThemeOptions(), configThemeOptions(tenant))

  if (tenant?.themeMode) {
    theme = merge(defaultThemeOptions(), darkThemeOptions(), configThemeOptions(tenant))
  }

  const darken = (color) =>
    Color(color)
      .darken(1 / 5)
      .hex()
  const darkPrimary = darken(theme.primaryColor)
  const darkSecondary = darken(theme.secondaryColor)

  const darkGray = '#222222'
  const mediumGray = '#555555'
  const lightGray = '#DDE0E5'
  const veryLightGray = '#f7f7f7'

  const groupColors = {}
  for (const [i, color] of ticketGroupColors.entries()) {
    groupColors[`ticket-group-${i + 1}-color`] = color
  }

  if (theme.secondaryButtonStyle === 'solid') {
    theme.secondaryButtonBorder = `2px solid ${theme.secondaryColor}`
    theme.secondaryButtonBackground = theme.secondaryColor
    theme.secondaryButtonTextColor = getContrastTextColor(theme.secondaryColor)
    theme.secondaryButtonBorderHover = darkSecondary
    theme.secondaryButtonBackgroundHover = darkSecondary
  } else {
    theme.secondaryButtonBorder = `2px solid ${lightGray}`
    theme.secondaryButtonBackground = 'white'
    theme.secondaryButtonTextColor = mediumGray
    theme.secondaryButtonBorderHover = '#bfc1ca'
    theme.secondaryButtonBackgroundHover = 'white'
  }

  theme.primaryContrastColor = getContrastTextColor(theme.primaryColor)

  const { width, height } = tenantTeaserImageSize(tenant)

  /* Direct raw options. */
  return {
    'teaser-image-width': `${width}px`,
    'teaser-image-height': `${height}px`,
    'primary-color': theme.primaryColor,
    'primary-contrast-color': theme.primaryContrastColor,
    'secondary-color': theme.secondaryColor,
    'header-background': theme.headerBackground,
    'member-links-color': theme.memberLinksColor,
    'member-links-color-hover': theme.memberLinksColorHover,
    'cart-header-background-color': theme.cartHeaderBackgroundColor,
    'cart-header-text-color': theme.cartHeaderTextColor,
    'cart-header-border': theme.cartHeaderBorder,
    'logo-height': theme.logoHeight,
    'footer-background-color': theme.footerBackgroundColor,
    'footer-text-color': theme.footerTextColor,
    'page-background-color': theme.pageBackgroundColor,
    'header-top-border': theme.headerTopBorder,
    'header-bottom-border': theme.headerBottomBorder,
    'header-drop-shadow': theme.headerDropShadow,
    'header-height': theme.headerHeight,
    'mobile-footer-height': '80px',

    'mobile-cart-header-height': '72px',
    'mobile-cart-footer-height': '180px',

    /* Derived options. */
    'primary-color-darker': darkPrimary,
    'secondary-color-darker': darkSecondary,

    /* Hard-coded colors. */
    black: 'black',
    'dark-gray': darkGray,
    'medium-gray': mediumGray,
    'light-gray': lightGray,
    'very-light-gray': veryLightGray,

    'info-color': theme.infoColor,
    'info-background-color': theme.infoBackgroundColor,
    'success-color': theme.successColor,
    'success-background-color': theme.successBackgroundColor,
    'warning-color': theme.warningColor,
    'warning-background-color': theme.warningBackgroundColor,
    'error-color': theme.errorColor,
    'error-background-color': theme.errorBackgroundColor,
    'promo-color': theme.promoColor,
    'promo-background-color': theme.promoBackgroundColor,

    'secondary-button-border': theme.secondaryButtonBorder,
    'secondary-button-background': theme.secondaryButtonBackground,
    'secondary-button-text-color': theme.secondaryButtonTextColor,
    'secondary-button-border-hover': theme.secondaryButtonBorderHover,
    'secondary-button-background-hover': theme.secondaryButtonBackgroundHover,

    /* Ticket group colors; Used in seating map and filters */
    ...groupColors,

    'dialog-background-color': theme.dialog.backgroundColor,
    'dialog-header-text-color': theme.dialog.headerTextColor,
    'dialog-header-background-color': theme.dialog.headerBackground,
    'dialog-header-icon-color': theme.dialog.headerIconColor,
    'dialog-header-border': theme.dialog.headerBorder,

    'mobile-footer-background-color': theme.mobileFooter.backgroundColor,
    'mobile-footer-text-color': theme.mobileFooter.textColor,
    'mobile-footer-cart-button-background-color': theme.mobileFooter.cartButtonBackgroundColor,
    'mobile-footer-cart-button-hover-background-color': theme.mobileFooter.cartButtonHoverBackgroundColor,
    'mobile-footer-cart-button-border': theme.mobileFooter.cartButtonBorder,
    'mobile-footer-cart-button-icon-color': theme.mobileFooter.cartButtonIconColor,
    'mobile-footer-cart-badge-background-color': theme.mobileFooter.cartBadgeBackgroundColor,
    'mobile-footer-cart-badge-text-color': theme.mobileFooter.cartBadgeTextColor,
    'mobile-footer-price-text-color': theme.mobileFooter.priceTextColor,

    'back-button-icon-color': theme.backButton.iconColor,
    'back-button-background-color': theme.backButton.backgroundColor,
    'back-button-shadow': theme.backButton.shadow,

    'date-picker-background-color': theme.datePicker.backgroundColor,
    'date-picker-button-color': theme.datePicker.buttonColor,
    'date-picker-button-icon-color': theme.datePicker.buttonIconColor,
    'date-picker-day-text-color': theme.datePicker.dayTextColor,
    'date-picker-today-background-color': theme.datePicker.todayBackgroundColor,
    'date-picker-selected-day-text-color': theme.datePicker.selectedDayTextColor,
    'date-picker-selected-day-background-color': theme.datePicker.selectedDayBackgroundColor,
    'date-picker-disabled-day-text-color': theme.datePicker.disabledDayTextColor,
    'date-picker-focused-day-background-color': theme.datePicker.focusedDayBackgroundColor,
    'date-picker-divider': theme.datePicker.divider,
    'date-picker-month-text-color': theme.datePicker.monthTextColor,
    'date-picker-weekday-text-color': theme.datePicker.weekdayTextColor,
    'date-picker-legend-text-color': theme.datePicker.weekdayTextColor,
  }
}

function getContrastTextColor(color) {
  return Color(color).isLight() ? 'var(--medium-gray)' : 'white'
}

function defaultThemeOptions(): ThemeOptions {
  const darkGray = '#222222'
  const mediumGray = '#555555'

  return {
    primaryColor: darkGray,
    primaryContrastColor: '',
    secondaryColor: darkGray,
    headerBackground: 'white',
    memberLinksColor: darkGray,
    memberLinksColorHover: darkGray,
    cartHeaderBackgroundColor: darkGray,
    cartHeaderTextColor: 'white',
    cartHeaderBorder: 'none',
    logoHeight: '32px',
    footerBackgroundColor: 'white',
    footerTextColor: mediumGray,
    pageBackgroundColor: 'white',
    headerTopBorder: 'none',
    headerBottomBorder: 'none',
    headerDropShadow: 'none',
    headerHeight: '105px',
    secondaryButtonStyle: 'outline',
    infoColor: '#3b73d8',
    infoBackgroundColor: '#e6f0fb',
    successColor: '#006400',
    successBackgroundColor: '#f5fffa',
    warningColor: '#ffa500',
    warningBackgroundColor: '#fff6e6',
    errorColor: '#eb1c26',
    errorBackgroundColor: '#fee',
    promoColor: '#9877b0',
    promoBackgroundColor: 'rgba(152, 119, 176, 0.02)',
    dialog: {
      backgroundColor: '#F7F9FA',
      headerTextColor: '#000000',
      headerBackground: '#ffffff',
      headerIconColor: '#555555',
      headerBorder: '1px solid #DDE0E6',
    },
    mobileFooter: {
      backgroundColor: '#ffffff',
      textColor: '#545557',
      cartButtonBackgroundColor: '#ffffff',
      cartButtonHoverBackgroundColor: '#ffffff',
      cartButtonBorder: '2px solid #BFC1CA',
      cartButtonIconColor: '#747981',
      cartBadgeBackgroundColor: '#ff0000',
      cartBadgeTextColor: '#ffffff',
      priceTextColor: '#545557',
    },
    backButton: {
      iconColor: '#282B37',
      backgroundColor: '#ffffff',
      shadow: '0 0 5px rgb(0 0 0 / 40%)',
    },
    datePicker: {
      backgroundColor: 'rgba(255, 255, 255, 0.1)',
      buttonColor: '#ffffff',
      buttonIconColor: '#282B37',
      dayTextColor: '#282B37',
      todayBackgroundColor: '#EFF0F5',
      selectedDayTextColor: '',
      selectedDayBackgroundColor: '',
      disabledDayTextColor: '#BFC1CA',
      focusedDayBackgroundColor: '',
      divider: '1px dashed #cccccc',
      monthTextColor: '#282B37',
      weekdayTextColor: '#525A6C',
      legendTextColor: '#525A6C',
    },
  }
}

function darkThemeOptions(): DeepPartial<ThemeOptions> {
  return {
    dialog: {
      backgroundColor: '#222433',
      headerTextColor: '#ffffff',
      headerBackground: '#292B3C',
      headerIconColor: '#A1A8AF',
      headerBorder: '0',
    },
    mobileFooter: {
      backgroundColor: '#292B3C',
      textColor: '#A1A8AF',
      cartButtonBackgroundColor: '#292B3C',
      cartButtonHoverBackgroundColor: '#292B3C',
      cartButtonBorder: '2px solid #37394B',
      cartButtonIconColor: '#ffffff',
      priceTextColor: '#E0E3E5',
    },
    backButton: {
      iconColor: '#ffffff',
      backgroundColor: '#008DEB',
      shadow: '0px 0px 5px rgba(0, 0, 0, 0.4)',
    },
    datePicker: {
      backgroundColor: 'rgba(34, 36, 51, 0.1)',
      buttonColor: '#A1A8AF',
      buttonIconColor: '#282B37',
      dayTextColor: '#E0E3E5',
      todayBackgroundColor: 'rgba(255,255,255,0.2)',
      selectedDayTextColor: '',
      selectedDayBackgroundColor: '',
      disabledDayTextColor: '#6B717A',
      focusedDayBackgroundColor: '',
      divider: '1px dashed #37394B',
      monthTextColor: '#E0E3E5',
      weekdayTextColor: '#A1A8AF',
      legendTextColor: '#A1A8AF',
    },
  }
}

export function configThemeOptions(tenant: TenantConfig): ThemeOptions | {} {
  if (tenant.themeOptions) {
    return tenant.themeOptions
  } else if (tenant.hasNoConfig || tenant.ticketureTheme) {
    // Ticketure branding.
    return {
      primaryColor: '#4694E6',
      secondaryColor: '#4694E6',
      headerBackground: '#003567',
      memberLinksColor: 'white',
      memberLinksColorHover: '#4694E6',
      cartHeaderBackgroundColor: '#003567',
      footerBackgroundColor: '#003567',
      footerTextColor: 'white',
      pageBackgroundColor: '#F7F7F7',
      headerDropShadow: '0px 0px 5px 0px rgba(0, 0, 0, 0.4)',
      datePicker: {
        selectedDayTextColor: '#ffffff',
      },
    }
  } else {
    return {
      // No config theme options for gen 3.1 (2020) and 3.2 (2021) themes.
      // They do not support theme colors in config.
    }
  }
}
